import {
    Autocomplete,
    Box,
    Checkbox,
    FormControl,
    FormGroup,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { areStringAnswerRequirementsMet } from '../../helpers/validator';

/**
 * Component to display question and receive input
 */

interface FieldTypeProps {
    answerKey: string;
    options: Array<string>;
    questionType: string;
    question?: string;
    getCurVal: (key: string) => string;
    handleChange: (key: string, value: any, keysToClear: string) => void;
    keysToClear: string; // these are the answer keys that need to be cleared if the answer to this question changes
    images?: Array<ImageButtonOption>; // optional for image buttons
    validation?: InputValidation; // validation currently only works on text
    focusOnNext?: () => void;
}

export interface InputValidateOptions {
    pattern?: string;
    maxLength?: number;
}

export interface InputValidation {
    inputValidationRules?: InputValidateOptions;
    errorMessage: String;
}

export interface ImageButtonOption {
    image: string;
    label: string;
    subtitle: string;
}

export const FieldType = ({
    answerKey,
    options,
    questionType,
    question,
    getCurVal,
    handleChange,
    keysToClear,
    images,
    validation,
    focusOnNext,
}: FieldTypeProps) => {
    switch (questionType) {
        case 'radio': {
            return (
                <FormControl sx={{ marginBottom: 2 }} component="fieldset">
                    <Typography sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                        {question}
                    </Typography>
                    <RadioGroup
                        sx={{ display: 'inline-flex' }}
                        onChange={async (e) => {
                            await handleChange(
                                answerKey,
                                e.target.value,
                                keysToClear
                            );
                            if (focusOnNext) focusOnNext();
                        }}
                        value={getCurVal(answerKey)}
                        name="radio-buttons-group"
                    >
                        {options.map((option: any) => {
                            return (
                                <Box key={option} sx={{ marginBottom: 1 }}>
                                    <FormControlLabel
                                        key={option}
                                        value={option}
                                        control={<Radio />}
                                        label={option}
                                    />
                                </Box>
                            );
                        })}
                    </RadioGroup>
                </FormControl>
            );
        }
        case 'text': {
            const areRequirementsMet = areStringAnswerRequirementsMet(
                getCurVal(answerKey),
                validation?.inputValidationRules
            );
            const helperText = areRequirementsMet
                ? ''
                : validation?.errorMessage;

            return (
                <>
                    <Typography sx={{ fontWeight: 'bold', marginBottom: 2 }}>
                        {question}
                    </Typography>
                    <TextField
                        autoFocus
                        label={'Type an answer'}
                        sx={{
                            width: '25ch',
                        }}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && focusOnNext) {
                                focusOnNext();
                            }
                        }}
                        error={!areRequirementsMet}
                        value={getCurVal(answerKey) || ''}
                        onChange={(e) =>
                            handleChange(answerKey, e.target.value, keysToClear)
                        }
                        helperText={helperText}
                        inputProps={{
                            autoComplete: 'off', // disable autocomplete and autofill
                            ...validation?.inputValidationRules,
                        }}
                    />
                </>
            );
        }
        case 'checkbox': {
            return (
                <FormControl sx={{ marginBottom: 2 }}>
                    {question && (
                        <Typography
                            sx={{ fontWeight: 'bold', marginBottom: 1 }}
                        >
                            {question}
                        </Typography>
                    )}
                    <FormGroup sx={{ display: 'inline-flex' }}>
                        {options.map((option: any) => {
                            return (
                                <Box key={option}>
                                    <FormControlLabel
                                        key={option}
                                        value={option}
                                        label={option}
                                        control={
                                            <Checkbox
                                                checked={
                                                    option ===
                                                    getCurVal(answerKey)
                                                }
                                                onChange={async (e) => {
                                                    await handleChange(
                                                        answerKey,
                                                        getCurVal(answerKey) ===
                                                            option
                                                            ? null
                                                            : option,
                                                        keysToClear
                                                    );
                                                    if (focusOnNext)
                                                        focusOnNext();
                                                }}
                                            />
                                        }
                                    />
                                </Box>
                            );
                        })}
                    </FormGroup>
                </FormControl>
            );
        }
        case 'searchSelect': {
            return (
                <>
                    <Typography sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                        {question}
                    </Typography>
                    <Autocomplete
                        disablePortal
                        value={getCurVal(answerKey) || null}
                        data-testid="search-select-text-input-field"
                        options={options}
                        sx={{ maxWidth: 300, marginBottom: 2 }}
                        getOptionLabel={(option: any) => option}
                        renderOption={(props, option: any) => (
                            <Box component="li" {...props}>
                                {option}
                            </Box>
                        )}
                        isOptionEqualToValue={(option, value) =>
                            option === value ||
                            value === 'Prefer not to disclose'
                        }
                        onChange={async (event, value) => {
                            await handleChange(answerKey, value, keysToClear);
                            if (focusOnNext) focusOnNext();
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select the answer"
                                inputProps={{
                                    ...params.inputProps,
                                    autoComplete: 'off', // disable autocomplete and autofill
                                }}
                            />
                        )}
                    />
                </>
            );
        }
        case 'imageButton': {
            const theme = useTheme();
            const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

            return (
                <>
                    <Typography sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                        {question}
                    </Typography>
                    <Typography sx={{ marginBottom: 1 }}>
                        Click the image to choose
                    </Typography>

                    <ImageList
                        sx={{ width: '100%' }}
                        cols={isDesktop ? 3 : 1}
                        children={(
                            images || [{ image: '', label: '', subtitle: '' }]
                        ).map((item, index) => (
                            <ImageListItem
                                key={index}
                                sx={{
                                    minWidth: '2rem',
                                    border:
                                        getCurVal(answerKey) === item.label
                                            ? '0.5rem solid #FF007A'
                                            : 'none',
                                }}
                                onClick={async (e) => {
                                    await handleChange(
                                        answerKey,
                                        item.label,
                                        keysToClear
                                    );
                                    if (focusOnNext) focusOnNext();
                                }}
                            >
                                <img
                                    src={item.image}
                                    srcSet={`${item.image}?w=248&fit=crop&auto=format&dpr=2 2x`}
                                    alt={item.label}
                                    loading="lazy"
                                />
                                <ImageListItemBar
                                    sx={{
                                        '& .MuiImageListItemBar-title ': {
                                            fontWeight: 'bold',
                                        },
                                    }}
                                    title={item.label}
                                    subtitle={item.subtitle}
                                />
                            </ImageListItem>
                        ))}
                    />
                </>
            );
        }
        default:
            return <></>;
    }
};
